import Button from '@/components/Button';
import Container from '@/components/Container';

import { GENERATED } from '@edwin-group/people-graphql';

import type {
  ComponentCommonEmbedded,
  ComponentDefaultContentTemplateTimer,
  ComponentTimerTimerAudio,
  ComponentTimerTimerAudioLabels,
  ComponentTimerTimerPrompt,
  ComponentTimerTimerPromptLabels,
  ComponentTimerTimerVideo,
  ComponentTimerTimerVideoLabels,
} from '@edwin-group/people-graphql';
import React, { useEffect, useRef, useState } from 'react';
import { CalmBreathingAnimation } from './BreathingWheel';
import classNames from 'classnames';
import DefaultContentTemplateEmbedded from '@/components/DefaultContentTemplate/Embedded';

import { PauseIcon, PlayIcon, StopIcon } from '@heroicons/react/20/solid';

import { Maybe } from 'graphql/jsutils/Maybe';

type ComponentProps = {
  timerType?: string;
  timerAudio?: Maybe<ComponentTimerTimerAudio>;
  timerAudioLabels?: Maybe<ComponentTimerTimerAudioLabels>;
  timerVideo?: Maybe<ComponentTimerTimerVideo>;
  timerVideoLabels?: Maybe<ComponentTimerTimerVideoLabels>;
  timerPrompt?: Maybe<ComponentTimerTimerPrompt>;
  timerPromptLabels?: Maybe<ComponentTimerTimerPromptLabels>;
} & ComponentDefaultContentTemplateTimer;

export default function Timer({ component }: { component: ComponentProps }) {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [isStarted, setIsStartedState] = useState(false);
  const [key, setKey] = React.useState(0);

  const { timerAudio, timerPrompt, timerVideo, timerAudioLabels, timerPromptLabels } = component || {};
  const type = component?.timerType;

  const [duration, setDuration] = useState<number | null>(null);

  const setIsStarted = (state: boolean) => {
    setIsStartedState(state);
  };

  const audioFile = timerAudio?.spokenGuidance?.data?.attributes?.url || '';
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (audioRef?.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsStarted(false);
  }, [key, audioRef]);

  useEffect(() => {
    if (type === GENERATED.Enum_Componentdefaultcontenttemplatetimer_Type.Prompt) {
      setDuration(timerPrompt?.durationInSeconds);
    }

    if (type === GENERATED.Enum_Componentdefaultcontenttemplatetimer_Type.Audio && audioFile) {
      audioRef.current = new Audio(audioFile);
      audioRef.current.onloadedmetadata = () => {
        setDuration(audioRef.current?.duration ?? null);
      };
    }
  }, [type, audioFile, timerPrompt?.durationInSeconds]);

  return (
    <div className="min-h-[46rem]">
      <Container className="mt-sh-60">
        <div className="col-span-12 mt-sh-15 gap-4 lg:col-start-3 lg:col-end-11">
          <div>
            {type === GENERATED.Enum_Componentdefaultcontenttemplatetimer_Type.Prompt ? (
              <>
                <div className=" grid grid-cols-1 xl:grid-cols-2">
                  <div className="flex items-center justify-center px-5">
                    <div className="flex flex-col items-center text-center">
                      <>
                        {timerPrompt?.title && !isFinished ? (
                          <h2 className="mb-sh-15 font-haffer text-sh-md">{timerPrompt?.title}</h2>
                        ) : null}
                        {timerPromptLabels?.thankYouTitle && isFinished ? (
                          <h2 className="mb-sh-15 font-haffer text-sh-md">{timerPromptLabels?.thankYouTitle}</h2>
                        ) : null}
                        {!isFinished && (
                          <div
                            className="text-center [&>p]:text-sh-14-18"
                            dangerouslySetInnerHTML={{
                              __html: timerPrompt?.prompt || '',
                            }}
                          />
                        )}

                        <div className="mt-sh-30 flex gap-sh-15">
                          {isStarted && !isFinished && (
                            <>
                              <Button
                                onClick={() => {
                                  if (isAnimating) {
                                    audioRef?.current?.pause();
                                  } else {
                                    audioRef?.current?.play();
                                  }
                                  setIsAnimating(!isAnimating);
                                }}
                                theme={'solid'}
                                className={classNames(
                                  'flex items-center justify-center transition duration-300 ease-in',
                                  {},
                                )}>
                                {isAnimating ? (
                                  <PauseIcon className="inline-block h-4 w-4" />
                                ) : (
                                  <PlayIcon className="inline-block h-4 w-4" />
                                )}
                                {isAnimating
                                  ? `${timerPromptLabels?.pauseButtonLabel || 'Pause'}`
                                  : `${timerPromptLabels?.playButtonLabel || 'Play'}`}
                              </Button>
                              <Button
                                onClick={() => {
                                  setIsAnimating(false);
                                  setKey((prevKey: number) => prevKey + 1);
                                }}
                                theme={'solid'}
                                className={classNames(
                                  'flex items-center justify-center transition duration-300 ease-in',
                                  {},
                                )}>
                                <StopIcon className="inline-block h-4 w-4" />
                                {timerPromptLabels?.stopButtonLabel || 'Stop'}
                              </Button>
                            </>
                          )}

                          {!isStarted && !isFinished && (
                            <Button
                              onClick={() => {
                                audioRef?.current?.play();
                                setIsStarted(true);
                                setIsAnimating(true);
                              }}
                              disabled={isAnimating}
                              theme={'solid'}
                              className={classNames(
                                'flex items-center justify-center transition duration-300 ease-in',
                                {},
                              )}>
                              {timerPromptLabels?.startExerciseButton || 'Start exercise'}
                            </Button>
                          )}

                          {isFinished && (
                            <Button
                              onClick={() => {
                                setIsStarted(true);
                                setKey((prevKey: number) => prevKey + 1);
                                setIsFinished(false);
                              }}
                              disabled={isAnimating}
                              theme={'solid'}
                              className={classNames(
                                'flex items-center justify-center transition duration-300 ease-in',
                                {},
                              )}>
                              {timerPromptLabels?.restartButtonLabel || 'Try the exercise again'}
                            </Button>
                          )}
                        </div>
                      </>
                    </div>
                  </div>
                  <div className="mt-10 flex items-center justify-center lg:mt-0">
                    {duration ? (
                      <CalmBreathingAnimation
                        key={key}
                        totalTime={duration}
                        isAnimating={isAnimating}
                        onFinish={() => {
                          setIsStarted(false);
                          setIsFinished(true);
                          setIsAnimating(false);
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
            {type === GENERATED.Enum_Componentdefaultcontenttemplatetimer_Type.Audio ? (
              <>
                <div className="mb-20 grid grid-cols-1 xl:grid-cols-2">
                  <div className="flex items-center justify-center px-5">
                    <div className="flex flex-col items-center text-center">
                      <>
                        {timerAudio?.title && !isFinished && (
                          <h2 className="mb-sh-15 font-haffer text-sh-md">{timerAudio?.title}</h2>
                        )}
                        {timerAudioLabels?.thankYouTitle && isFinished ? (
                          <h2 className="mb-sh-15 font-haffer text-sh-md">{timerAudioLabels?.thankYouTitle}</h2>
                        ) : null}
                        <div
                          className="text-center [&>p]:text-sh-14-18"
                          dangerouslySetInnerHTML={{
                            __html:
                              (type === GENERATED.Enum_Componentdefaultcontenttemplatetimer_Type.Audio &&
                                timerAudio?.prompt) ||
                              '',
                          }}
                        />

                        <div className="mt-sh-30 flex gap-sh-15">
                          {isStarted && !isFinished && (
                            <>
                              <Button
                                onClick={() => {
                                  if (isAnimating) {
                                    audioRef?.current?.pause();
                                  } else {
                                    audioRef?.current?.play();
                                  }
                                  setIsAnimating(!isAnimating);
                                }}
                                theme={'solid'}
                                className={classNames(
                                  'flex items-center justify-center transition duration-300 ease-in',
                                  {},
                                )}>
                                {isAnimating ? (
                                  <PauseIcon className="inline-block h-4 w-4" />
                                ) : (
                                  <PlayIcon className="inline-block h-4 w-4" />
                                )}
                                {isAnimating
                                  ? `${timerAudioLabels?.pauseButtonLabel || 'Pause'}`
                                  : `${timerAudioLabels?.playButtonLabel || 'Play'}`}
                              </Button>
                              <Button
                                onClick={() => {
                                  setIsAnimating(false);
                                  setKey((prevKey: number) => prevKey + 1);
                                }}
                                theme={'solid'}
                                className={classNames(
                                  'flex items-center justify-center transition duration-300 ease-in',
                                  {},
                                )}>
                                <StopIcon className="inline-block h-4 w-4" />
                                {timerAudioLabels?.stopButtonLabel || 'Stop'}
                              </Button>
                            </>
                          )}

                          {!isStarted && !isFinished && (
                            <Button
                              onClick={() => {
                                setIsStarted(true);
                                audioRef?.current?.play();
                                setIsAnimating(true);
                              }}
                              disabled={isAnimating}
                              theme={'solid'}
                              className={classNames(
                                'flex items-center justify-center transition duration-300 ease-in',
                                {},
                              )}>
                              {timerAudioLabels?.startExerciseButton || 'Start exercise'}
                            </Button>
                          )}

                          {isFinished && (
                            <Button
                              onClick={() => {
                                setIsStarted(true);
                                setKey((prevKey: number) => prevKey + 1);
                                setIsFinished(false);
                              }}
                              disabled={isAnimating}
                              theme={'solid'}
                              className={classNames(
                                'flex items-center justify-center transition duration-300 ease-in',
                                {},
                              )}>
                              {timerPromptLabels?.restartButtonLabel || 'Try the exercise again'}
                            </Button>
                          )}
                        </div>
                      </>
                    </div>
                  </div>
                  <div className="mt-10 flex items-center justify-center lg:mt-0">
                    {duration ? (
                      <CalmBreathingAnimation
                        key={key}
                        totalTime={duration}
                        isAnimating={isAnimating}
                        onFinish={() => {
                          setIsFinished(true);
                          setIsAnimating(false);
                        }}
                      />
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
            {type === GENERATED.Enum_Componentdefaultcontenttemplatetimer_Type.Video ? (
              <>
                <div className="grid grid-cols-1">
                  <div className="flex items-center justify-center">
                    <div className="relative flex w-full flex-col items-center text-center">
                      {timerVideo?.videoTitle ? (
                        <h2 className="mb-sh-15 font-haffer text-sh-md">{timerVideo?.videoTitle}</h2>
                      ) : null}
                      {timerVideo?.video?.data?.attributes && (
                        <DefaultContentTemplateEmbedded
                          className="mindful-video !my-0"
                          onVideoPlay={() => {
                            if (!isStarted) {
                              setIsStarted(true);
                            }
                          }}
                          component={
                            {
                              id: 'DefaultContentTemplateEmbedded',
                              link: timerVideo?.video?.data?.attributes?.url,
                              provider: GENERATED.Enum_Componentcommonembedded_Provider.Youtube,
                            } as ComponentCommonEmbedded
                          }
                          onDuration={(n) => {
                            setDuration(n);
                          }}
                          onEnded={() => {
                            setIsFinished(true);
                            setIsAnimating(false);
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </Container>
    </div>
  );
}
